import React, { useContext, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { ActionPlanEditorProps } from '../../../ActionPlan/ActionPlanEditor';
import { CollaboratorType } from '../../../../enums/CollaboratorType';
import { ContactSchema, RoomSchema } from '../../../../definitions/schemas';
import { EventProperty } from '../../../../tools/Analytics';
import { generateCollaborator } from '../../../ActionPlan/helper';
import { Properties, Referrers } from '../../../../enums/Analytics';
import { QUERY_STRING } from '../../../../enums/QueryString';
import { RoomViewContext } from '../../View/RoomViewContext';
import { ShareRoomProps } from '../../../ActionPlan/Tasks/Owners/types';
import AnonymousIdentityGuard from '../../View/AnonymousIdentityGuard';
import RoomActionPlans from './RoomActionPlans';
import RoomActionPlansContextProvider from './RoomActionPlansContext';

export type PickedActionPlanEditorProps = Pick<
  ActionPlanEditorProps,
  'allowInvitations' | 'disableNavigationBlock' | 'hideOwnerAndDue' | 'viewOnly'
>;

interface Props {
  ActionPlanEditorProps: PickedActionPlanEditorProps;
  referrer: string;
  room: RoomSchema;
  trackingProperties: EventProperty;
  userContactId?: string;
}

const RoomActionPlansWrapper: React.FC<Props> = (props: Props): JSX.Element => {
  const { isGuestVisitor, queryStringParams = {} } =
    useContext(RoomViewContext);

  const {
    ActionPlanEditorProps,
    referrer,
    room,
    trackingProperties,
    userContactId,
  } = props;

  const [actionPlans, setActionPlans] = useState(room.actionPlans || []);
  const [invitedContacts, setInvitedContacts] = useState<ContactSchema[]>([]);

  const { current: actionPlanReferrer } = useRef(
    `${referrer}-${Referrers.ACTION_PLAN.TASK_OWNER}`
  );

  const actionPlanCollaboratorOptions = useMemo(
    () =>
      invitedContacts.map((contact) =>
        generateCollaborator(CollaboratorType.Contact, contact)
      ),
    [invitedContacts]
  );

  const getActionPlanShareRoomDialogProps = (): ShareRoomProps => ({
    contactId: userContactId,
    label: isGuestVisitor ? 'peer' : 'contact',
    onContactsInvited,
    roomId: room.id,
    trackingProperties: {
      ...trackingProperties,
      // overwriting the referrer to be something
      // like: "Deal Room Contact View-MAP Task Owner"
      [Properties.ENTERED_FROM]: actionPlanReferrer,
    },
  });

  /**
   * Handler called when the user invites more contacts via sharing the
   * room with them. This stores those invited contacts to make them immediately
   * available as collaborator options for the MAP task owner selection
   */
  const onContactsInvited = (contacts?: ContactSchema[]) => {
    if (contacts) {
      setInvitedContacts((prev) => [...prev, ...contacts]);
    }
  };

  return (
    <Box pt={1}>
      <AnonymousIdentityGuard
        referrer={Referrers.DEAL_ROOMS.ACTIVITY.ACTION_PLAN}
      >
        <RoomActionPlansContextProvider
          {...ActionPlanEditorProps}
          allowDiscussion
          collaborators={actionPlanCollaboratorOptions}
          companyName={room?.deal?.company?.name}
          deepLinkActionPlanId={queryStringParams[QUERY_STRING.ACTION_PLAN_ID]}
          deepLinkActionPlanTaskId={
            queryStringParams[QUERY_STRING.ACTION_PLAN_TASK_ID]
          }
          deepLinkActionPlanTaskMessageId={
            queryStringParams[QUERY_STRING.ACTION_PLAN_TASK_MSG_ID]
          }
          displayFeedbackImmediately
          isGuestVisitor={isGuestVisitor}
          organizationName={room.organization?.name}
          referrer={referrer}
          roomActionPlans={actionPlans}
          roomId={room.id}
          roomUUID={room.uuid}
          setActionPlans={setActionPlans}
          ShareRoomDialogProps={getActionPlanShareRoomDialogProps()}
          userContactId={userContactId}
        >
          <RoomActionPlans
            actionPlanLabel={room.organization?.actionPlanLabel}
            disablePublishWarning
            roomActionPlans={actionPlans}
            setRoomActionPlans={setActionPlans}
          />
        </RoomActionPlansContextProvider>
      </AnonymousIdentityGuard>
    </Box>
  );
};

export default RoomActionPlansWrapper;
